import {
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  Text,
  useToast,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React from 'react';

import { Util } from '@finley-technologies/finley-types';

import { CreditAgreementDownloadGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import {
  errorToastId,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { useGetCreditAgreementDownloadUrlQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { HiOutlineDownload } from 'react-icons/hi';

const downloadFile = (downloadUrl: string, fileName: string) => {
  return axios({
    url: downloadUrl,
    method: 'GET',
    responseType: 'blob',
  }).then((response) => {
    const fileBlob = new Blob([response.data], {
      type: response.data.type,
    });
    FileSaver.saveAs(fileBlob, fileName);
  });
};

const CreditAgreementDownloadButton = ({
  creditAgreementS3Path,
}: {
  creditAgreementS3Path: Util.Maybe<CreditAgreementDownloadGQLType>;
}) => {
  const toast = useToast();
  const errorToastTitle = 'Error downloading the credit agreement';
  if (!creditAgreementS3Path) {
    if (!toast.isActive(errorToastId)) {
      errorToastConfig.title = errorToastTitle;
      toast(errorToastConfig);
    }
    throw errorToastConfig.title;
  }

  return (
    <Icon
      data-testid="download-credit-agreement-button"
      cursor="pointer"
      onClick={() => {
        Sentry.addBreadcrumb({
          category: 'ui.click',
          message: `Clicked download for credit agreement`,
          level: Sentry.Severity.Info,
        });

        downloadFile(
          creditAgreementS3Path.downloadUrl,
          creditAgreementS3Path.fileName,
        ).catch((err) => {
          if (!toast.isActive(errorToastId)) {
            errorToastConfig.title = errorToastTitle;
            toast(errorToastConfig);
          }
          throw err;
        });
      }}
      as={HiOutlineDownload}
      marginBottom="3px"
    />
  );
};

export const CreditAgreementDownload = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const {
    loading: creditAgreementUrlLoading,
    data: creditAgreementUrlData,
  } = useGetCreditAgreementDownloadUrlQuery({
    variables: { creditFacilityId },
    fetchPolicy: 'no-cache',
  });

  const isCreditAgreementUrlLoading =
    creditAgreementUrlLoading ||
    !creditAgreementUrlData ||
    !creditAgreementUrlData.getCreditAgreementS3Path;

  return (
    <>
      <Text mb="40px">
        Finley has extracted and summarized the key terms of your credit
        agreement. If you&apos;d like to reference your credit agreement
        documents, you can download them below.
      </Text>

      <Table
        variant="simple"
        size="md"
        width={{
          sm: '100%',
          md: '50%',
        }}
        borderWidth="1px"
      >
        <Thead
          background={colors.tableHeader}
          borderBottom="1px solid lightgrey"
          padding="8px 0"
        >
          <Tr>
            <Th width="80%">Document</Th>
            <Th width="20%" textAlign="center">
              Download
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr className="reports-row">
            <Td>Credit Agreement</Td>
            <Td textAlign="center">
              {isCreditAgreementUrlLoading ? (
                <Spinner
                  thickness="1px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="sm"
                />
              ) : (
                <CreditAgreementDownloadButton
                  creditAgreementS3Path={
                    creditAgreementUrlData.getCreditAgreementS3Path
                  }
                />
              )}
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </>
  );
};
